import * as React from 'react';

import { Button, Switch, TextField } from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

export type ProcessingTypeInputItemData = {
  id: string;
  existingId?: string;
  itemId: string;
  isPrimary: boolean;
  sortOrder: number;
  notes?: string;
};

type ProcessingTypeInputItemRowProps = {
  item: ProcessingTypeInputItemData;
  index: number;
  onUpdate: (index: number, data: Partial<ProcessingTypeInputItemData>) => void;
  onRemove: (index: number) => void;
  isDisabled?: boolean;
};

export const ProcessingTypeInputItemRow = ({
  item,
  index,
  onUpdate,
  onRemove,
  isDisabled = false,
}: ProcessingTypeInputItemRowProps) => {
  const t = useTranslations();

  const handleItemChange = React.useCallback(
    (itemId?: string) => {
      onUpdate(index, { itemId: itemId || '' });
    },
    [index, onUpdate],
  );

  const handleIsPrimaryChange = React.useCallback(
    (value: boolean) => {
      onUpdate(index, { isPrimary: value });
    },
    [index, onUpdate],
  );

  const handleNotesChange = React.useCallback(
    (value: string) => {
      onUpdate(index, { notes: value });
    },
    [index, onUpdate],
  );

  const handleRemove = React.useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <div className="@ProcessingTypeInputItemRow grid grid-cols-[1fr_80px_1fr_40px] items-start gap-2 rounded border border-neutral/20 p-3">
      <SelectMaterial
        placeholder={t.processingTypesView.dialog.recipe.selectItem}
        value={item.itemId || undefined}
        onChange={handleItemChange}
        isDisabled={isDisabled}
      />
      <div className="flex items-center justify-center pt-2">
        <Switch
          isSelected={item.isPrimary}
          onChange={handleIsPrimaryChange}
          isDisabled={isDisabled}
          aria-label={t.processingTypesView.dialog.recipe.isPrimary}
        />
      </div>
      <TextField
        placeholder={t.processingTypesView.dialog.recipe.notes}
        value={item.notes || ''}
        onChange={handleNotesChange}
        isDisabled={isDisabled}
      />
      <Button
        variant="ghost"
        color="failure"
        icon={<Trash2 size={16} />}
        onPress={handleRemove}
        isDisabled={isDisabled}
        aria-label={t.processingTypesView.dialog.recipe.removeInput}
      />
    </div>
  );
};
